import { UNITS, UNICODE_FRACTIONS, formatQuantity } from './ingredient-parse.js';
/**
 * Serving-scaling for a recipe's amounts — a pure function shared by the recipe
 * view, cook mode and the add-to-list step, so every surface scales identically.
 * Nothing scaled is ever stored: the recipe keeps its base amounts and the
 * target servings are a view state.
 *
 * `scaleIngredients` rescales the structured ingredient list; `scaleSteps`
 * rescales the amounts a person wrote *inside* a step's prose, which are not
 * structured data and would otherwise stay at the base amount after a rescale.
 *
 * A null quantity ("snufje zout", "scheut olie") is not a measurement and passes
 * through a rescale untouched.
 */
/**
 * Rounds a scaled amount to something a person would actually measure: whole
 * numbers for larger amounts, halves in the middle, quarters below one. (200 g ×
 * 1.5 = 300; 2 eieren × 1.5 = 3; ½ ui × 1.5 = 0.75.)
 */
export function roundQuantity(n) {
    if (!Number.isFinite(n))
        return n;
    const abs = Math.abs(n);
    const step = abs >= 20 ? 1 : abs >= 1 ? 0.5 : 0.25;
    return Math.round((Math.round(n / step) * step) * 100) / 100;
}
export function scaleQuantity(quantity, base, target) {
    if (quantity == null)
        return null;
    if (!(base > 0) || !(target > 0) || base === target)
        return quantity;
    return roundQuantity((quantity * target) / base);
}
export function scaleIngredients(ingredients, base, target) {
    if (base === target)
        return ingredients;
    return ingredients.map((ing) => ({ ...ing, quantity: scaleQuantity(ing.quantity, base, target) }));
}
/* ---------------- step text ---------------- */
/**
 * Amounts a person wrote *inside* a step's prose ("voeg 500 g bloem toe", "klop
 * 2 eieren los") are free text, so `scaleIngredients` never reaches them — after
 * a rescale the step would still quote the base amount. This rescales those
 * in-prose amounts at render, view-only like everything else here.
 *
 * It only touches a number immediately followed by a **known unit** (the shared
 * Dutch unit list) or by a **noun the recipe's own ingredients use** (so "2
 * eieren" scales when *eieren* is an ingredient, "4 borden" never does). Times
 * and temperatures ("20 minuten", "180 °C") are in neither list and are left
 * exactly as written. A generic count word that is also a unit ("in 4 stukken")
 * is the one accepted false positive.
 */
export function scaleStepText(text, base, target, nouns) {
    if (!(base > 0) || !(target > 0) || base === target)
        return text;
    const words = [...UNITS, ...nouns].filter((w) => w.length > 0).sort((a, b) => b.length - a.length);
    if (words.length === 0)
        return text;
    const alt = words.map(escapeRegExp).join('|');
    // <number><optional space><unit-or-noun>, the word matched to its end (\b) so
    // `liter` beats `l` and a unit welded inside a longer word never matches.
    const re = new RegExp(`(\\d+\\s*/\\s*\\d+|\\d+(?:[.,]\\d+)?|[½¼¾⅓⅔⅕⅛])(\\s*)(${alt})\\b`, 'gi');
    return text.replace(re, (match, numStr, sep, word) => {
        const value = readStepNumber(numStr);
        if (value == null)
            return match;
        const scaled = scaleQuantity(value, base, target);
        if (scaled == null)
            return match;
        return `${formatQuantity(scaled)}${sep}${word}`;
    });
}
/**
 * Scales the in-prose amounts of every step. The nouns a count may scale against
 * are derived from the recipe's own ingredient names — the codebase's "from
 * their own data" rule — so a recipe only ever rescales counts of things it
 * actually lists.
 */
export function scaleSteps(steps, ingredients, base, target) {
    if (!(base > 0) || !(target > 0) || base === target)
        return steps;
    const nouns = ingredientNouns(ingredients);
    return steps.map((s) => ({ ...s, text: scaleStepText(s.text, base, target, nouns) }));
}
/**
 * The lowercased nouns a step may scale a count of: every word in the recipe's
 * ingredient names, with light singular/plural variants, minus the units (which
 * the shared unit list already covers).
 */
function ingredientNouns(ingredients) {
    const nouns = new Set();
    for (const ing of ingredients) {
        for (const raw of ing.name.toLowerCase().split(/[^a-zà-ÿ]+/)) {
            if (raw.length < 2 || UNITS.has(raw))
                continue;
            addWordVariants(nouns, raw);
        }
    }
    return nouns;
}
const IRREGULAR_PLURALS = { ei: 'eieren', eieren: 'ei' };
/**
 * Adds a word and its likely singular/plural partners, so a step saying "uien"
 * is caught from an ingredient named "ui" and vice versa. Over-generating a form
 * that never appears is harmless — a wrong scale still needs a real number sat
 * beside the word.
 */
function addWordVariants(set, w) {
    set.add(w);
    const irregular = IRREGULAR_PLURALS[w];
    if (irregular)
        set.add(irregular);
    if (w.endsWith('en') && w.length > 4)
        set.add(w.slice(0, -2));
    else if (!w.endsWith('en'))
        set.add(`${w}en`);
    if (w.endsWith('s') && w.length > 3)
        set.add(w.slice(0, -1));
    else if (!w.endsWith('s'))
        set.add(`${w}s`);
}
/** Reads a number token the step scanner matched: `1`, `1,5`/`1.5`, `1/2`, or a unicode fraction. */
function readStepNumber(s) {
    const t = s.trim();
    if (UNICODE_FRACTIONS[t] !== undefined)
        return UNICODE_FRACTIONS[t];
    const frac = /^(\d+)\s*\/\s*(\d+)$/.exec(t);
    if (frac) {
        const d = Number(frac[2]);
        return d ? Number(frac[1]) / d : null;
    }
    if (/^\d+(?:[.,]\d+)?$/.test(t))
        return Number(t.replace(',', '.'));
    return null;
}
function escapeRegExp(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
//# sourceMappingURL=scale.js.map